import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ChevronRight, CreditCard, Star, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MembershipSelect = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      id: "monthly",
      name: "Monthly",
      price: "$29.99",
      period: "/mo",
      description: "Flexible month-to-month access",
      features: [
        "Full provider directory access",
        "Schedule lunches & meetings",
        "Unlimited messaging",
        "Meeting scheduling tools",
        "Email notifications"
      ],
      popular: false,
      savings: null,
    },
    {
      id: "quarterly",
      name: "Quarterly",
      price: "$59.99",
      period: "/qtr",
      description: "Save 33% with quarterly billing",
      features: [
        "Everything in Monthly",
        "Analytics dashboard",
        "Priority support",
      ],
      popular: true,
      savings: "Save 33%",
    },
    {
      id: "annual",
      name: "Annual",
      price: "$199.99",
      period: "/yr",
      description: "Best value — save 44%",
      features: [
        "Everything in Quarterly",
        "Priority support",
        "Best price per month",
      ],
      popular: false,
      savings: "Save 44%",
    },
  ];

  const handleSelectPlan = async (planId) => {
    setLoading(planId);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/membership/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      }, { headers });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Unable to start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg"
            alt="iConcierge Logo"
            className="h-10 w-auto"
          />
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-slate-500 text-sm"
            data-testid="skip-membership-btn"
          >
            Skip for now
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-4xl w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Choose Your Membership
            </h1>
            <p className="text-slate-600 max-w-lg mx-auto">
              Unlock the full iConcierge platform — schedule meetings, send unlimited messages, manage referrals, and grow your network.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl p-6 border-2 transition-all ${
                  plan.popular
                    ? "border-primary shadow-xl ring-1 ring-primary/20"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="inline-flex items-center gap-1 bg-primary text-white text-xs font-medium px-3 py-1 rounded-full mb-4">
                    <Star className="w-3 h-3" />
                    Recommended
                  </div>
                )}
                {plan.savings && !plan.popular && (
                  <div className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-3 py-1 rounded-full mb-4">
                    {plan.savings}
                  </div>
                )}
                {!plan.popular && !plan.savings && <div className="h-7 mb-4" />}

                <h3 className="text-lg font-bold text-slate-900 mb-1">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-3">{plan.description}</p>

                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-3xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-slate-500">{plan.period}</span>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full rounded-full ${
                    plan.popular
                      ? "bg-primary text-white hover:bg-primary/90"
                      : "bg-slate-100 text-slate-900 hover:bg-slate-200"
                  }`}
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loading !== null}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {loading === plan.id ? "Redirecting..." : "Select Plan"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-slate-400 mt-8">
            Secure payment powered by Square. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MembershipSelect;
